<script lang="ts" setup generic="TValue extends PropertyKey">
import { provide, ref, toRef, toRefs, watch, watchEffect } from "vue";
import { useDensity } from "../../composables/density.js";
import {
  SKELETON_INJECTED_SYMBOL,
  useSkeletonContext,
} from "../../composables/useSkeletonState.js";
import { useVModel } from "../../composables/useVModel.js";
import {
  ACCORDION_INJECTION_KEY,
  type AccordionInjectionKey,
  type OnyxAccordionProps,
} from "./types.js";

const props = withDefaults(defineProps<OnyxAccordionProps<TValue>>(), {
  exclusive: false,
  disabled: false,
  skeleton: SKELETON_INJECTED_SYMBOL,
  type: "default",
});

const emit = defineEmits<{
  /**
   * Emitted when the list of open items changes.
   */
  "update:modelValue": [value: TValue[]];
}>();

defineSlots<{
  /**
   * Use the default slot to pass one or more `OnyxAccordionItem`.
   */
  default(): unknown;
}>();

/**
 * Internally managed open items in case no modelValue is set by the user.
 */
const _openItems = ref<TValue[]>([]);
watchEffect(() => (_openItems.value = props.modelValue ?? []));

const openItems = useVModel({
  // if modelValue is set by the user, it should be used instead of the internally managed open items
  props,
  emit,
  key: "modelValue",
  default: () => [],
});

const { disabled, exclusive } = toRefs(props);
const skeleton = useSkeletonContext(props);
const { densityClass } = useDensity(props);

const updateOpen = (value: TValue, isOpen: boolean) => {
  if (!isOpen) {
    if (!openItems.value.includes(value)) return;
    openItems.value = openItems.value.filter((i) => i !== value);
    return;
  }
  if (exclusive.value) {
    openItems.value = [value];
    return;
  }
  if (!openItems.value.includes(value)) {
    openItems.value = openItems.value.concat(value);
  }
};

watch(
  exclusive,
  (newExclusive) => {
    if (newExclusive && openItems.value.length > 1) {
      const lastOpenedItem = openItems.value.at(-1);
      openItems.value = lastOpenedItem != undefined ? [lastOpenedItem] : [];
    }
  },
  { immediate: true },
);

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- false positive
provide(ACCORDION_INJECTION_KEY as AccordionInjectionKey<TValue>, {
  openItems: toRef(() => openItems.value),
  type: toRef(() => props.type),
  updateOpen,
  disabled,
  skeleton,
});
</script>

<template>
  <div
    :class="[
      'onyx-component',
      'onyx-accordion',
      densityClass,
      { 'onyx-accordion--card': props.type === 'card' },
    ]"
  >
    <slot></slot>
  </div>
</template>

<style lang="scss">
@use "../../styles/mixins/layers";

.onyx-accordion {
  @include layers.component() {
    width: 100%;

    &--card {
      --onyx-accordion-gap: var(--onyx-density-xs);
      display: flex;
      flex-direction: column;
      gap: var(--onyx-accordion-gap);
    }
  }
}
</style>
